/*
 * Corectura scrierii înainte de salvare (1 nov 2026, Dan): „mecanicul să vadă textul corectat și să confirme; să apară
 * un tab clar, vizibil, cu cele două variante și buton de aprobă; dacă trebuie să corecteze ceva, să poată corecta —
 * de ex. dacă în propunere e și o greșeală”.
 *
 * `confirmaScrierea` trimite textele la server (`/api/corectura/propune`, services/corectura_text.py). Dacă ceva s-a
 * corectat, arată ecranul „Verifică scrierea”: ce a scris omul (cu cuvintele schimbate marcate) și propunerea,
 * într-un câmp pe care îl poate îndrepta, cu „✓ Aprobă” mare și „Păstrez ce am scris” dedesubt. Întoarce textele de
 * salvat. Salvarea nu se blochează niciodată: fără răspuns de la server (sau după 25 s), textele pleacă cum sunt.
 */
import { escHtml } from './core';

export interface CampDeVerificat {
    eticheta: string;      // „Problema”, „Constatări”, „Observația ta”…
    text: string;
}

interface Propunere { texte?: string[]; schimbat?: boolean[]; activa?: boolean }

const ARE_LITERE = /[A-Za-zĂÂÎȘȚăâîșțŞŢşţ]{2,}/;

/** Cuvintele din `original` care nu mai sunt în `propus` (ordinea contează; LCS pe cuvinte), marcate. */
function _marcheazaSchimbarile(original: string, propus: string): string {
    const a = original.split(/(\s+)/), b = propus.split(/(\s+)/);
    const n = a.length, m = b.length;
    const L: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
    for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--)
        L[i][j] = a[i] === b[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
    let i = 0, j = 0, out = '';
    while (i < n) {
        if (j < m && a[i] === b[j]) { out += escHtml(a[i]); i++; j++; }
        else if (j < m && L[i][j + 1] >= L[i + 1][j]) j++;
        else { out += /^\s+$/.test(a[i]) ? escHtml(a[i]) : `<mark class="cor-schimbat">${escHtml(a[i])}</mark>`; i++; }
    }
    return out;
}

function _asteptare(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'cor-asteptare';
    el.setAttribute('role', 'status');
    el.textContent = 'Verific scrierea…';
    el.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:10050;'
        + 'background:var(--card-bg);border:1px solid var(--border);border-radius:10px;padding:10px 18px;'
        + 'color:var(--text);box-shadow:0 6px 20px rgba(0,0,0,.35);font-size:.95rem';
    document.body.appendChild(el);
    return el;
}

function _dialog(campuri: CampDeVerificat[], propuse: string[], schimbat: boolean[]): Promise<string[]> {
    return new Promise(resolve => {
        const overlay = document.createElement('div');
        overlay.className = 'proc-overlay cor-overlay';
        const blocuri = campuri.map((c, k) => !schimbat[k] ? '' :
            `<div class="cor-camp" style="display:flex;flex-direction:column;gap:6px">` +
            `<div style="font-weight:700;color:var(--text)">${escHtml(c.eticheta)}</div>` +
            `<div class="cor-variante" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:10px">` +
            `<div><div style="font-size:.8rem;color:var(--text-muted);margin-bottom:4px">Ce ai scris</div>` +
            `<div class="cor-original" style="white-space:pre-wrap;padding:10px;border:1px solid var(--border);` +
            `border-radius:8px;color:var(--text-muted);min-height:48px">${_marcheazaSchimbarile(c.text, propuse[k])}</div></div>` +
            `<div><div style="font-size:.8rem;color:var(--green, #22c55e);margin-bottom:4px">Propunere corectată — o poți ` +
            `îndrepta</div><textarea class="dx-input cor-propus" data-k="${k}" rows="3" ` +
            `style="min-height:72px;width:100%;box-sizing:border-box;border-color:var(--green, #22c55e)"></textarea></div>` +
            `</div></div>`).join('');
        overlay.innerHTML =
            '<div class="proc-container" style="max-width:760px" role="dialog" aria-modal="true" aria-labelledby="cor-titlu">' +
            '<div class="proc-header"><div class="proc-header-top">' +
            '<h2 class="proc-title" id="cor-titlu">Verifică scrierea</h2></div></div>' +
            '<div style="padding:16px;display:flex;flex-direction:column;gap:14px">' +
            '<div style="color:var(--text-muted);font-size:.92rem">Am corectat literele, diacriticele și cuvintele ' +
            'schimbate de telefon. Uită-te la propunere — dacă vezi o greșeală, îndreapt-o în câmpul verde — apoi ' +
            'apasă <b>Aprobă</b>.</div>' + blocuri +
            '<div style="display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap;align-items:center">' +
            '<button class="dx-btn-primary" id="cor-pastreaza" style="background:var(--card-bg);border:1px solid var(--border);' +
            'color:var(--text-muted);padding:12px 18px;min-height:48px">Păstrez ce am scris</button>' +
            '<button class="dx-btn-primary" id="cor-aproba" style="padding:12px 30px;min-height:52px;font-size:1.05rem">' +
            '✓ Aprobă</button></div></div></div>';
        document.body.appendChild(overlay);
        overlay.querySelectorAll<HTMLTextAreaElement>('.cor-propus').forEach(t => {
            t.value = propuse[Number(t.dataset.k)];
        });
        const gata = (v: string[]) => { overlay.remove(); resolve(v); };
        overlay.querySelector('#cor-aproba')!.addEventListener('click', () => {
            const final = campuri.map(c => c.text);
            overlay.querySelectorAll<HTMLTextAreaElement>('.cor-propus').forEach(t => {
                const v = t.value.trim();
                final[Number(t.dataset.k)] = v || campuri[Number(t.dataset.k)].text;
            });
            gata(final);
        });
        overlay.querySelector('#cor-pastreaza')!.addEventListener('click', () => gata(campuri.map(c => c.text)));
        setTimeout(() => (overlay.querySelector('#cor-aproba') as HTMLElement | null)?.focus(), 50);
    });
}

/** Textele de salvat, după ce omul a văzut propunerea (sau neschimbate, dacă nu era nimic de corectat). */
export async function confirmaScrierea(campuri: CampDeVerificat[], ecran = ''): Promise<string[]> {
    const texte = campuri.map(c => c.text || '');
    if (!texte.some(t => ARE_LITERE.test(t))) return texte;
    let p: Propunere | null = null;
    const asteapta = _asteptare();
    try {
        const ctrl = new AbortController();
        const termen = setTimeout(() => ctrl.abort(), 25000);
        const r = await fetch('/api/corectura/propune', {
            method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ texte }), signal: ctrl.signal,
        });
        clearTimeout(termen);
        p = r.ok ? await r.json() : null;
    } catch {
        p = null;
    } finally {
        asteapta.remove();
    }
    const propuse = p?.texte, schimbat = p?.schimbat;
    if (!Array.isArray(propuse) || !Array.isArray(schimbat) || propuse.length !== texte.length
        || !schimbat.some(Boolean)) return texte;
    const final = await _dialog(campuri.map((c, k) => ({ eticheta: c.eticheta, text: texte[k] })), propuse, schimbat);
    // ce a ales omul — originalul rămâne în `texte_corectate` (fără să întârzie salvarea)
    fetch('/api/corectura/decizie', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            ecran, campuri: campuri.map((c, k) => ({ eticheta: c.eticheta, original: texte[k], propus: propuse[k],
                final: final[k] })).filter((_, k) => schimbat[k]),
        }),
    }).catch(() => { /* jurnalul deciziei nu oprește nimic */ });
    return final;
}

/** Varianta pentru un singur text. */
export async function confirmaUnText(eticheta: string, text: string, ecran = ''): Promise<string> {
    return (await confirmaScrierea([{ eticheta, text }], ecran))[0];
}
